import React from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import { formatCountyStateLabel, formatDetectedCourtName } from '../lib/court-display';

interface CardData {
  id: 'court' | 'case-type';
  icon: string;
  title: string;
  lines: string[];
  detected: boolean;
  addManuallyLabel: string;
  /** Detected but not yet actionable (e.g. a case category with no specific type).
   *  Suppresses the "Looks right" affordance — confirming it would commit nothing
   *  downstream — and offers the in-step path to finish it instead (WOL-329 6b). */
  incomplete?: boolean;
  /** Shown when incomplete: what's still missing and why it matters. */
  incompleteNote?: string;
}

interface OcrConfirmCardsProps {
  brand: BrandConfig;
  county: string;
  state: string;
  caseCategoryName: string;
  caseTypeName: string;
  /** Whether the case type resolved to a court policy *code* (not just a name).
   *  Downstream (the document-type list) needs the code, so a name without a
   *  code is not actually "done" — keep the card incomplete until it resolves. */
  caseTypeResolved: boolean;
  ocrPopulatedFields: Set<string>;
  /** IDs of cards the user has confirmed ("Looks right"), persisted in wizard state. */
  confirmedFields: string[];
  /** Commit a card confirmation to wizard state. */
  onConfirm: (cardId: string) => void;
  /**
   * Open the card's IN-CARD picker (Walter R26). Every affordance that used to
   * navigate to a later wizard step — "Change" on a detected card, the
   * "Select court" / "Set case type" fallbacks — now asks DocumentsTab to
   * force-mount the card's inline selector instead: nothing on the Upload
   * step may jump ahead before per-document types are set.
   */
  onOpenPicker: (cardId: 'court' | 'case-type') => void;
  /**
   * In-card case category/type picker (Walter 2026-07-10 R9–R11). When the
   * case type still needs setting (or needs re-picking — WOL-330/335/345),
   * DocumentsTab passes the InlineCaseTypeSelector here and the Case Category
   * & Type card renders it in place — the SINGLE location for that selection
   * on the Upload step. Replaces the former top-of-page "Set your case type to
   * continue" box and the scroll-up "Set case type →" affordance.
   */
  caseTypeSlot?: React.ReactNode;
  /**
   * In-card court picker (Walter 2026-07-10 R14–R17). When OCR couldn't read
   * the court, DocumentsTab passes the InlineCourtSelector here and the Court
   * card renders it in place — so the root of the document-typing dependency
   * chain (county → categories/types → document types) is settable on the
   * Upload step itself, never requiring a jump ahead to Where to File and
   * back. Replaces the "Not detected → Select court →" navigate-away
   * affordance whenever present.
   */
  courtSlot?: React.ReactNode;
  /**
   * Render the detected court as a proper court name — "Superior Court of
   * California, County of Madera" instead of the short "Madera, CA" label
   * (Walter R72, delivered flag-gated per R80). DocumentsTab passes
   * `confirmFlowEnabled`. Either way the label is title-cased (Walter R109):
   * the classic card renders `formatCountyStateLabel`, never the raw
   * lowercase county slug.
   */
  useProperCourtName?: boolean;
}

const OcrConfirmCards: React.FC<OcrConfirmCardsProps> = ({
  brand,
  county,
  state,
  caseCategoryName,
  caseTypeName,
  caseTypeResolved,
  ocrPopulatedFields,
  confirmedFields,
  onConfirm,
  onOpenPicker,
  caseTypeSlot,
  courtSlot,
  useProperCourtName,
}) => {

  // Only the two field-level cards render here (Walter 2026-07-10 R22): the
  // former Parties and Attorney cards' "Add parties →" / "Add attorney →"
  // affordances jumped the filer forward to About Your Case — off the one task
  // this step still needs (per-document typing). Parties and attorneys are
  // handled on About Your Case (roster + the R20 Document Assignments
  // section), so they get no card on the Upload step at all.
  const cards: CardData[] = [
    {
      id: 'court',
      icon: '📍', // pin emoji
      title: 'Court',
      lines: county
        ? [
            useProperCourtName
              ? formatDetectedCourtName(county, state)
              : formatCountyStateLabel(county, state),
          ]
        : [],
      detected: ocrPopulatedFields.has('county'),
      addManuallyLabel: 'Select court',
    },
    {
      id: 'case-type',
      icon: '📋', // clipboard emoji
      title: 'Case Category & Type',
      // Label the lines so a bare category isn't mistaken for a specific type.
      //
      // SOR-194: the Type line additionally requires the type to have RESOLVED,
      // the same way the Category line requires a court-recognised name
      // (SOR-173). An unresolved type is exactly the state in which this card
      // mounts the picker saying "We couldn't read the case type from your
      // document" — so a `Type:` line above it contradicts it inside one card,
      // which is what Walter saw. `caseTypeResolved` is already the declared
      // answer to "did this reach a court code"; read it rather than printing
      // whatever name arrived.
      lines: [
        ...(caseCategoryName ? [`Category: ${caseCategoryName}`] : []),
        ...(caseTypeName && caseTypeResolved ? [`Type: ${caseTypeName}`] : []),
      ],
      detected: ocrPopulatedFields.has('caseCategoryName') || ocrPopulatedFields.has('caseTypeName'),
      // The document-type list keys off the resolved case-type *code*. A bare
      // category, or a type name we couldn't match to a court code, can't load
      // it — so it isn't something to "confirm" yet. Keep it incomplete (no
      // decorative "Looks right") and point at the inline selector.
      incomplete: !caseTypeName || !caseTypeResolved,
      incompleteNote: caseTypeName
        ? `We read "${caseTypeName}" but couldn't match it to this court's case types — pick the exact one to continue.`
        : 'Case type still needed — pick a specific type to load document types.',
      addManuallyLabel: 'Set case type',
    },
  ];

  return (
    <div
      id="ocr-confirm-cards"
      data-testid="ocr-confirm-cards"
      className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6"
    >
      {cards.map(card => {
        const isConfirmed = confirmedFields.includes(card.id);
        const hasData = card.detected && card.lines.length > 0;
        // Only meaningful once we have partial data to show; a fully-undetected
        // card falls through to the "Not detected" + set-manually path.
        const incomplete = !!card.incomplete && hasData;
        // The in-card picker replaces every other affordance for its card:
        // its own reason copy covers the incomplete note, and selecting in
        // place makes "Not detected → set manually" moot (case-type: R9–R11;
        // court: R14–R17 — both selections happen ON this step, never via a
        // jump to a later tab and back).
        const slot = card.id === 'case-type' ? caseTypeSlot
          : card.id === 'court' ? courtSlot
            : undefined;
        const hasSlot = !!slot;

        return (
          <div
            key={card.id}
            data-testid={`ocr-card-${card.id}`}
            className="border p-5 transition-colors"
            style={{
              borderRadius: 12,
              borderColor: isConfirmed ? (brand.colors.success || '#22C55E') : brand.colors.border,
              backgroundColor: isConfirmed ? '#F0FDF4' : brand.colors.bgWhite,
            }}
          >
            {/* Header */}
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg" role="img" aria-hidden="true">{card.icon}</span>
              <span
                className="text-sm font-semibold"
                style={{ color: brand.colors.text }}
              >
                {card.title}
              </span>
              {isConfirmed && (
                <span
                  className="ml-auto inline-flex items-center justify-center w-5 h-5 rounded-full text-white text-xs font-bold"
                  style={{ backgroundColor: brand.colors.success || '#22C55E' }}
                  aria-label="Confirmed"
                >
                  {'✓'}
                </span>
              )}
            </div>

            {/* Content — detected (extraction-sourced) lines */}
            {hasData ? (
              <div className="mb-3" data-testid={`ocr-card-${card.id}-detected`}>
                {card.lines.map((line) => (
                  <p
                    key={line}
                    className="text-sm leading-relaxed"
                    style={{ color: brand.colors.text }}
                  >
                    {line}
                  </p>
                ))}
              </div>
            ) : !hasSlot ? (
              <p
                className="text-sm mb-3 italic"
                style={{ color: brand.colors.textLight }}
              >
                Not detected
              </p>
            ) : null}

            {/* In-card picker (court or case category/type) — the single place
                to set that selection on this step. Carries its own reason copy,
                so the incomplete note and the set-manually affordance are
                suppressed. */}
            {hasSlot && slot}

            {/* Incomplete note — partial detection that still needs finishing */}
            {!hasSlot && incomplete && card.incompleteNote && (
              <p
                className="text-xs mb-3 font-medium"
                style={{ color: brand.colors.warning || '#F59E0B' }}
              >
                {card.incompleteNote}
              </p>
            )}

            {/* Actions. "Looks right" disappears once the card is confirmed,
                but "Change" SURVIVES confirmation (SOR-162): a wrong court or
                case type must stay correctable in place on the Upload step —
                confirming a card must not make it terminal and force the filer
                to advance to Confirm and back. Activating "Change" re-opens the
                in-card selector (onOpenPicker). The addManually fallback (not
                detected / incomplete) stays gated on !isConfirmed as before. */}
            {!hasSlot && (hasData && !incomplete ? (
              <div className="flex items-center gap-2">
                {!isConfirmed && (
                  <button
                    onClick={() => onConfirm(card.id)}
                    className="px-3 py-1.5 rounded-md text-xs font-medium text-white transition-colors hover:opacity-90"
                    style={{ backgroundColor: brand.colors.success || '#22C55E' }}
                  >
                    Looks right
                  </button>
                )}
                <button
                  onClick={() => onOpenPicker(card.id)}
                  className="px-3 py-1.5 rounded-md text-xs font-medium border transition-colors hover:opacity-80"
                  style={{
                    borderColor: brand.colors.border,
                    color: brand.colors.textLight,
                  }}
                >
                  Change
                </button>
              </div>
            ) : !isConfirmed ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onOpenPicker(card.id)}
                  className="px-3 py-1.5 rounded-md text-xs font-medium transition-colors hover:opacity-80"
                  style={{
                    backgroundColor: `${brand.colors.primary}10`,
                    color: brand.colors.link,
                  }}
                >
                  {card.addManuallyLabel}
                </button>
              </div>
            ) : null)}
          </div>
        );
      })}
    </div>
  );
};

export default OcrConfirmCards;
